/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';

import { useAvatarManifest } from '../../assets/data/avatarManifest';
import { AvatarAppearanceV1, avatarSlotOrder, specialZIndexConfig } from '../../utils/interfaces/DbInterfaces';
import { ICompDefault } from '../../utils/interfaces/TypesInterfaces';

interface ICompanionCanvasProps extends ICompDefault {
  appearance: AvatarAppearanceV1;
  width?: number;
  height?: number;
  colors?: Record<string, string>;
}

const DEFAULT_WIDTH = 334;
const DEFAULT_HEIGHT = 422;

// Simple unique ID generator since useId is not available in this React version
let idCounter = 0;
const generateId = () => {
  idCounter += 1;
  return `companion-${idCounter}`;
};

const CompanionCanvas: React.FC<ICompanionCanvasProps> = React.memo(({ appearance, width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT, colors = {} }) => {
  const uniqueId = useMemo(() => generateId(), []);
  const avatarManifest = useAvatarManifest();

  // Compose ordered layers including mood
  const layers = useMemo(() => {
    const result: { id: string; href: string; slot: string; z: number }[] = [];
    let moodLayer: { id: string; href: string; slot: string; z: number } | null = null;

    // Base always first
    if (appearance.base && avatarManifest[appearance.base]) {
      const z = specialZIndexConfig[appearance.base]?.zIndex ?? avatarSlotOrder.indexOf('base');
      result.push({ id: appearance.base, href: avatarManifest[appearance.base].svg, slot: 'base', z });
    }

    // Other slots in z-order, but separate mood to render last
    avatarSlotOrder.forEach((slot, index) => {
      if (slot === 'base') return; // Already handled
      const id = appearance.layers?.[slot];
      if (id && avatarManifest[id]) {
        const href = avatarManifest[id].svg;
        const z = specialZIndexConfig[id]?.zIndex ?? index;
        if (slot === 'mood') {
          moodLayer = { id, href, slot, z: 999 }; // Always on top
        } else {
          result.push({ id, href, slot, z });
        }
      }
    });

    // Sort by z-index (lowest first)
    result.sort((a, b) => a.z - b.z);

    // Add mood last so it renders on top
    if (moodLayer) {
      result.push(moodLayer);
    }

    return result;
  }, [appearance, avatarManifest]);

  // CSS vars for colors
  const styleVars = useMemo(() => {
    const vars: Record<string, string> = {};
    Object.entries(colors).forEach(([k, v]) => {
      vars[`--${k}`] = v;
    });
    return vars;
  }, [colors]);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ ...styleVars, background: 'transparent', overflow: 'visible' }}
      xmlns='http://www.w3.org/2000/svg'
    >
      <defs>
        {layers.map((layer) => (
          <g id={`${uniqueId}-${layer.id}`} key={layer.id}>
            {layer.slot === 'mood' ? null : <image href={layer.href} width={width} height={height} x={0} y={0} />}
            {/* {layer.slot === 'mood' ? (
              // Mood with thought bubble background - responsive sizing
              (() => {
                // Scale factor based on canvas size (40px is small, 120+ is normal)
                const isSmall = width <= 50;

                // Different sizing for small vs large
                const bubbleRadius = isSmall ? 8.5 : 12;
                const smallBubbleRadius = isSmall ? 2 : 2.5;
                const tinyBubbleRadius = isSmall ? 1.1 : 1.5;
                const iconSize = isSmall ? 12 : 16;

                // Position adjustments for small vs normal size
                const rightOffset = isSmall ? 2 : 16;
                const topOffset = isSmall ? 2 : 15;
                const smallBubbleOffsetX = isSmall ? 10 : 27;
                const smallBubbleOffsetY = isSmall ? 10 : 26;
                const tinyBubbleOffsetX = isSmall ? 12 : 31;
                const tinyBubbleOffsetY = isSmall ? 12 : 30;
                // Move icon more to top-right for small version
                const iconOffsetX = isSmall ? 8 : 24;
                const iconOffsetY = isSmall ? -4 : 7;

                return (
                  <>
                    <circle cx={width - rightOffset} cy={topOffset} r={bubbleRadius} fill='white' stroke='#e0e0e0' strokeWidth='1' opacity='0.95' />
                    <circle
                      cx={width - smallBubbleOffsetX}
                      cy={smallBubbleOffsetY}
                      r={smallBubbleRadius}
                      fill='white'
                      stroke='#e0e0e0'
                      strokeWidth='1'
                      opacity='0.95'
                    />
                    <circle
                      cx={width - tinyBubbleOffsetX}
                      cy={tinyBubbleOffsetY}
                      r={tinyBubbleRadius}
                      fill='white'
                      stroke='#e0e0e0'
                      strokeWidth='1'
                      opacity='0.95'
                    />
                    <image href={layer.href} width={iconSize} height={iconSize} x={width - iconOffsetX} y={iconOffsetY} />
                  </>
                );
              })()
            ) : (
              // Regular avatar layers fill the entire canvas
              <image href={layer.href} width={width} height={height} x={0} y={0} />
            )} */}
          </g>
        ))}
      </defs>
      {layers.map((layer) => (
        <use key={layer.id} href={`#${uniqueId}-${layer.id}`} />
      ))}
    </svg>
  );
});

CompanionCanvas.displayName = 'CompanionCanvas';

export default CompanionCanvas;
